import { useMediaQuery } from '@mui/material';

// Quarks
import { media } from 'atoms/breakpoints/breakpoints';
import color from 'atoms/colors/colors';

import Flex from 'quarks/Flex';
import Image from 'quarks/Image';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentButton from 'components/Button/ComponentButton';
import ComponentHeading from 'components/Heading/Heading';

import richTextParser from 'utils/richTextParser';

// Types
import type { ContentfulComponentFeatureGrid } from 'graphqlTypes';
import type { FC } from 'react';

interface FeatureGridProps extends FlexProps, ContentfulComponentFeatureGrid {
  backgroundIsDark: boolean;
  featureGridHeading?: ContentfulComponentFeatureGrid['heading'];
  isReferenced?: boolean;
}

const ComponentFeatureGrid: FC<FeatureGridProps> = ({
  headingAllignment,
  featureGridHeading,
  backgroundIsDark,
  feature,
  isReferenced,
  citedSources,
  ...props
}) => {
  // Heading Split & Center
  const isSplit = headingAllignment === 'Left';
  const isDesktop = useMediaQuery(media.lg);
  const cardHasCta = feature?.some(item => item?.callToAction);

  return (
    <>
      <Flex
        width="100%"
        flexDirection="column"
        justifyContent="space-between"
        gap="32px"
        {...props}
        md={{ gap: '48px' }}
        lg={{ flexDirection: isSplit ? 'row' : 'column', gap: '64px' }}
        css={`
          .MuiButton-linkPrimary,
          .MuiButton-link {
            color: ${backgroundIsDark ? color.common.white : color.gray[900]};

            &:hover {
              color: ${color.green[300]};
            }
          }
        `}
      >
        {featureGridHeading && (
          <Flex
            width="100%"
            maxWidth="100%"
            lg={{ maxWidth: isSplit ? '470px' : '100%' }}
            css={`
              div {
                align-items: ${isDesktop && isSplit ? 'left' : 'center'};
                justify-content: ${isDesktop && isSplit ? 'flex-start' : 'center'};
                text-align: ${isDesktop && isSplit ? 'left' : 'center'};
              }
            `}
          >
            <ComponentHeading
              backgroundIsDark={backgroundIsDark}
              {...featureGridHeading}
              isLeft={isDesktop ? isSplit : false}
            />
          </Flex>
        )}
        {feature && feature.length > 0 && (
          <Flex
            gap="20px"
            justifyContent={isReferenced ? 'flex-start' : 'center'}
            flexWrap="wrap"
            width="100%"
            maxWidth="100%"
            sm={{ gap: '30px' }}
          >
            {feature.map(
              item =>
                item && (
                  <Flex
                    key={item?.id}
                    flexDirection="column"
                    gap="16px"
                    paddingAll={24}
                    width="100%"
                    maxWidth="100%"
                    overflowX="hidden"
                    overflowY="hidden"
                    borderRadius="16px"
                    className={`relative border border-solid ${
                      backgroundIsDark ? 'border-[#7275AA]' : 'border-[#F3F3F3]'
                    } ${item.classNames || ''}`}
                    md={{ maxWidth: 'calc(50% - 15px)' }}
                    lg={{ maxWidth: isSplit ? 'calc((100% - 30px) / 2)' : 'calc((100% - 90px)/ 4)' }}
                  >
                    {item?.icon && (
                      <Image
                        image={item?.icon}
                        alt={item?.icon?.title || 'Feature Grid Icon'}
                        objectFit="cover"
                        objectPosition="center"
                        width="30px"
                        height="30px"
                      />
                    )}
                    <Flex
                      flexDirection="column"
                      gap="8px"
                      paddingBottom={cardHasCta ? 48 : 0}
                      css={`
                        div {
                          margin: 0;
                        }
                      `}
                    >
                      {item?.heading2 && (
                        <Text
                          textStyle="textMd"
                          fontWeight="semiBold"
                          className={`[&>h1]:mb-6 ${item?.headingClassNames || ''}`}
                          textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
                        >
                          {item.heading2 && richTextParser(item.heading2)}
                        </Text>
                      )}
                      {item?.body && (
                        <Flex
                          fontSize="textSm"
                          lineHeight="textSm"
                          textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
                          marginBottom={12}
                        >
                          {richTextParser(item.body)}
                        </Flex>
                      )}
                      {item?.callToAction && (
                        <Flex
                          css={`
                            position: absolute;
                            bottom: 20px;

                            .MuiButton-root {
                              width: fit-content !important;
                            }
                          `}
                        >
                          <ComponentButton key={item.callToAction?.contentful_id} {...item.callToAction} />
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                ),
            )}
          </Flex>
        )}
        {citedSources && (
          <Flex
            flexDirection="column"
            gap="16px"
            fontSize="textSm"
            lineHeight="textSm"
            textColor={backgroundIsDark ? 'common-white' : 'gray-700'}
            marginBottom={12}
          >
            {richTextParser(citedSources)}
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default ComponentFeatureGrid;
